import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Button,
  Alert,
  Card,
  IconButton,
  Chip,
  Divider,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  PhoneIphone as PhoneIphoneIcon,
  SettingsRemote as SettingsRemoteIcon,
  Palette as PaletteIcon,
  Edit as EditIcon,
  CameraAlt as CameraAltIcon,
  Image as ImageIcon,
  RestartAlt as RestartAltIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
} from '@mui/icons-material';
import { getMachineRandomCode, MachineRandomCodeResponse } from '../../services/api';
import { currentAppOrigin, SOCKET_URL, API_URL } from '../../config/api';
import { toast } from 'react-toastify';
import { io, Socket } from 'socket.io-client';
import { QRCodeSVG } from 'qrcode.react';
import { alpha } from '@mui/material/styles';
import { useI18n } from '../../i18n/useI18n';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { isStaffUser } from '../../utils/staffUser';
import { getDeviceMgmtFeatures } from '../../utils/deviceMgmtFeatures';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AppAccentColorApps from '../shared/AppAccentColorApps';
import MachineQrPositionEditor from '../shared/MachineQrPositionEditor';
import PhotoboothSimpleMode from '../shared/PhotoboothSimpleMode';
import MobileUploadDeviceLimit from '../shared/MobileUploadDeviceLimit';
import StaffUnlimitedQrCard from '../shared/StaffUnlimitedQrCard';
import MachineScopedProPhotos from '../shared/MachineScopedProPhotos';

const softMetaChipSx = {
  height: 28,
  borderRadius: '999px',
  fontWeight: 700,
  fontSize: '0.72rem',
  letterSpacing: '0.02em',
  border: '1px solid',
  '& .MuiChip-label': { px: 1.1 },
} as const;

function statusChipSx(running: boolean) {
  const tone = running ? '#059669' : '#e11d48';
  return {
    ...softMetaChipSx,
    bgcolor: alpha(tone, 0.1),
    color: tone,
    borderColor: alpha(tone, 0.28),
  };
}

function platformChipSx() {
  const tone = '#0f3460';
  return {
    ...softMetaChipSx,
    bgcolor: alpha(tone, 0.06),
    color: tone,
    borderColor: alpha(tone, 0.16),
  };
}

function productChipSx(product: string | null | undefined) {
  const p = String(product || '').trim().toLowerCase();
  const tone =
    p === 'lite' ? '#0284c7' : p === 'mono' ? '#4b5563' : p === 'pro' ? '#c2410c' : '#6b7280';
  return {
    ...softMetaChipSx,
    bgcolor: alpha(tone, 0.1),
    color: tone,
    borderColor: alpha(tone, 0.25),
  };
}

const pinkOutlineBtnSx = {
  color: '#bf5f82',
  fontWeight: 700,
  borderRadius: 999,
  textTransform: 'none' as const,
  border: '1px solid transparent',
  backgroundColor: 'transparent',
  backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(135deg, #ffc1d4, #ff8ba7, #f48fb1)',
  backgroundOrigin: 'padding-box, border-box',
  backgroundClip: 'padding-box, border-box',
};

const MachineQrCard: React.FC<{
  value: string;
  onOpen: () => void;
}> = ({ value, onOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const qrSize = isMobile ? 196 : 280;

  return (
    <Box display="flex" justifyContent="center" alignItems="center" mb={2} px={{ xs: 0.5, sm: 0 }}>
      <Card
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 3,
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          lineHeight: 0,
          maxWidth: '100%',
          width: 'fit-content',
          mx: 'auto',
          border: '1px solid transparent',
          backgroundColor: 'transparent',
          backgroundImage:
            'linear-gradient(#ffffff, #fff8fb), linear-gradient(135deg, #ffc1d4, #ff8ba7 55%, #f48fb1)',
          backgroundOrigin: 'padding-box, border-box',
          backgroundClip: 'padding-box, border-box',
          boxShadow: '0 12px 28px rgba(191, 95, 130, 0.16)',
          '&:hover': { transform: 'scale(1.02)', boxShadow: '0 16px 32px rgba(191, 95, 130, 0.22)' },
          '& svg': {
            display: 'block',
            margin: '0 auto',
            maxWidth: '100%',
            height: 'auto',
          },
        }}
        onClick={onOpen}
      >
        <QRCodeSVG value={value} size={qrSize} level="M" includeMargin />
      </Card>
    </Box>
  );
};

const MobileUploadMachineDetail: React.FC = () => {
  const { pages: t } = useI18n();
  const { user } = useSelector((state: RootState) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { machineCode } = useParams<{ machineCode: string }>();
  const navigate = useNavigate();
  const [machineData, setMachineData] = useState<MachineRandomCodeResponse['machine'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const socketRef = useRef<Socket | null>(null);
  const [remoteAction, setRemoteAction] = useState<'restart' | 'shutdown' | null>(null);
  const [remoteSending, setRemoteSending] = useState(false);

  const backendOrigin = API_URL.replace(/\/api\/?$/i, '');

  useEffect(() => {
    setActiveTab(0);
  }, [machineData?.appProduct, machineCode]);

  useEffect(() => {
    if (machineCode) {
      loadMachineData();
      setupSocketConnection();

      // Cleanup khi unmount
      return () => {
        if (socketRef.current) {
          socketRef.current.emit('leave-machine-room', machineCode.toUpperCase());
          socketRef.current.disconnect();
          socketRef.current = null;
        }
      };
    }
  }, [machineCode]);

  const setupSocketConnection = () => {
    if (!machineCode) return;

    // Tạo socket connection
    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      path: '/socket.io',
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ Connected to Socket.IO server');
      
      // Join machine room để nhận updates
      socket.emit('join-machine-room', machineCode.toUpperCase());
      console.log(`📡 Joined machine room: machine-${machineCode.toUpperCase()}`);
    });

    socket.on('disconnect', () => {
      console.log('❌ Disconnected from Socket.IO server');
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
    });

    socket.on('random-code-updated', (data: {
      machineCode: string;
      randomCode: string;
      fullCode: string;
      randomCodeUpdatedAt: string;
    }) => {
      console.log('📨 Received random-code-updated event:', data);
      
      // Cập nhật state với dữ liệu mới
      setMachineData(prevData => {
        if (prevData && prevData.machineCode === data.machineCode) {
          return {
            ...prevData,
            randomCode: data.randomCode,
            fullCode: data.fullCode,
            randomCodeUpdatedAt: data.randomCodeUpdatedAt,
          };
        }
        return prevData;
      });

      // Hiển thị thông báo
      toast.success(`Mã random code đã được cập nhật: ${data.randomCode}`, {
        position: 'top-right',
        autoClose: 3000,
      });
    });

    socket.on('control-page:ui-enabled-changed', (data: {
      machineCode?: string;
      controlPageUiEnabled?: boolean;
    }) => {
      if (!data?.machineCode) return;
      setMachineData((prev) => {
        if (prev && prev.machineCode.toUpperCase() === data.machineCode!.toUpperCase()) {
          return {
            ...prev,
            controlPageUiEnabled: data.controlPageUiEnabled !== false,
          };
        }
        return prev;
      });
    });

    socket.on('app-status-update', (data: {
      machineCode: string;
      isAppRunning: boolean;
      appPlatform?: 'windows' | 'android' | null;
      appProduct?: 'lite' | 'mono' | 'pro' | null;
    }) => {
      if (!data?.machineCode) return;
      setMachineData((prev) => {
        if (prev && prev.machineCode.toUpperCase() === data.machineCode.toUpperCase()) {
          return {
            ...prev,
            isAppRunning: data.isAppRunning,
            appPlatform: data.isAppRunning
              ? (data.appPlatform ?? prev.appPlatform)
              : prev.appPlatform,
            appProduct: data.isAppRunning
              ? (data.appProduct ?? prev.appProduct)
              : prev.appProduct,
          };
        }
        return prev;
      });
    });
  };

  const loadMachineData = async () => {
    if (!machineCode) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getMachineRandomCode(machineCode);
      if (response.success) {
        setMachineData(response.machine);
      } else {
        setError('Không thể tải thông tin máy');
      }
    } catch (err: any) {
      console.error('Error loading machine data:', err);
      setError(err.response?.data?.error || 'Không thể tải thông tin máy');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`Đã copy ${label}`);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Không thể copy');
    }
  };

  const sendRemoteAppCommand = (action: 'restart' | 'shutdown') => {
    const mc = (machineCode || machineData?.machineCode || '').trim().toUpperCase();
    const socket = socketRef.current;
    if (!mc || !socket?.connected) {
      toast.error(t.deviceMgmt.appOfflineCantRemote);
      return;
    }
    if (!machineData?.isAppRunning) {
      toast.error(t.deviceMgmt.appOfflineCantRemote);
      return;
    }
    setRemoteSending(true);
    try {
      socket.emit('device-mgmt-camera-command', {
        machineCode: mc,
        property: action === 'restart' ? 'restart-app' : 'shutdown-app',
        value: 0,
      });
      toast.success(action === 'restart' ? t.deviceMgmt.restartSent : t.deviceMgmt.shutdownSent);
      setRemoteAction(null);
    } catch {
      toast.error(t.deviceMgmt.appOfflineCantRemote);
    } finally {
      setRemoteSending(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !machineData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/mobile-upload')}
          sx={{ mb: 2 }}
        >
          {t.deviceMgmt.backShort}
        </Button>
        <Alert severity="error">{error || t.deviceMgmt.notFound}</Alert>
      </Container>
    );
  }

  const uploadPageUrl = `${currentAppOrigin()}/mobile-upload/machine/${machineData.fullCode}`;
  const controlPageUrl = `${backendOrigin}/controlpage/${machineData.machineCode}`;
  const editPageUrl = `${backendOrigin}/editpage/${machineData.machineCode}`;
  const { showUpload, showControl, showEdit, showPhotos, showPhotobooth, showQrEdit, showColorApps } =
    getDeviceMgmtFeatures(machineData.appProduct, { isStaffUser: isStaffUser(user) });
  const showControlTab = showControl && machineData.controlPageUiEnabled !== false && !isStaffUser(user);
  const tabKeys = [
    ...(showControlTab ? (['control'] as const) : []),
    ...(showPhotos ? (['photos'] as const) : []),
    ...(showUpload ? (['upload'] as const) : []),
    ...(showPhotobooth ? (['photobooth'] as const) : []),
    ...(showEdit ? (['edit'] as const) : []),
    ...(showQrEdit ? (['qrEdit'] as const) : []),
    ...(showColorApps ? (['colorApps'] as const) : []),
  ];
  const safeTabIndex = Math.min(activeTab, Math.max(tabKeys.length - 1, 0));
  const photosActive = tabKeys[safeTabIndex] === 'photos';
  const isConnected = !!machineData.isAppRunning;

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: photosActive ? 0 : { xs: 2, md: 4 },
        mb: photosActive ? 0 : 4,
        ...(photosActive
          ? {
              height: { xs: 'calc(100dvh - 56px)', sm: 'calc(100dvh - 64px)' },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxSizing: 'border-box',
              pt: { xs: 1, sm: 1.5 },
              pb: 1,
            }
          : {}),
      }}
    >
      <Box mb={photosActive ? 1 : 2} sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.25,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
            title={isConnected ? t.deviceMgmt.running : t.deviceMgmt.paused}
            sx={{
              textAlign: 'left',
              fontSize: { xs: '1.35rem', sm: '2.125rem' },
              lineHeight: 1.25,
              px: 0.5,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: {
                xs: isConnected ? '#059669' : '#e11d48',
                sm: 'text.primary',
              },
              ...(isConnected
                ? {
                    '@keyframes machinePulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.45 },
                    },
                    animation: {
                      xs: 'machinePulse 1.4s ease-in-out infinite',
                      sm: 'none',
                    },
                  }
                : {}),
            }}
          >
            {machineData.machineName || machineData.machineCode}
          </Typography>

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.75,
              flexShrink: 0,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={!isMobile ? <RestartAltIcon /> : undefined}
              disabled={!isConnected || remoteSending}
              onClick={() => setRemoteAction('restart')}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                minWidth: { xs: 40, sm: 'auto' },
                px: { xs: 1, sm: 1.25 },
              }}
              aria-label={t.deviceMgmt.restartApp}
            >
              {isMobile ? <RestartAltIcon fontSize="small" /> : t.deviceMgmt.restartApp}
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={!isMobile ? <PowerSettingsNewIcon /> : undefined}
              disabled={!isConnected || remoteSending}
              onClick={() => setRemoteAction('shutdown')}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                minWidth: { xs: 40, sm: 'auto' },
                px: { xs: 1, sm: 1.25 },
              }}
              aria-label={t.deviceMgmt.shutdownApp}
            >
              {isMobile ? <PowerSettingsNewIcon fontSize="small" /> : t.deviceMgmt.shutdownApp}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 0.75,
              px: 1,
              py: 0.65,
              borderRadius: 999,
              bgcolor: (th) => alpha(th.palette.grey[500], 0.06),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.divider, 0.9),
            }}
          >
            <Chip
              size="small"
              variant="outlined"
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'currentColor',
                      flexShrink: 0,
                    }}
                  />
                  {isConnected ? t.deviceMgmt.running : t.deviceMgmt.paused}
                </Box>
              }
              sx={{
                ...statusChipSx(isConnected),
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            />
            {machineData.appPlatform && (
              <Chip
                size="small"
                variant="outlined"
                label={
                  machineData.appPlatform === 'android'
                    ? t.deviceMgmt.platformAndroid
                    : t.deviceMgmt.platformWindows
                }
                sx={platformChipSx()}
              />
            )}
            {machineData.appProduct && (
              <Chip
                size="small"
                variant="outlined"
                label={
                  machineData.appProduct === 'mono'
                    ? t.deviceMgmt.productMono
                    : machineData.appProduct === 'pro'
                      ? t.deviceMgmt.productPro
                      : t.deviceMgmt.productLite
                }
                sx={productChipSx(machineData.appProduct)}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          mt: photosActive ? 1 : 3,
          ...(photosActive
            ? {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }
            : {}),
          borderRadius: 3,
          overflow: photosActive ? 'hidden' : 'visible',
          border: '1px solid transparent',
          backgroundColor: 'transparent',
          backgroundImage:
            'linear-gradient(165deg, #ffffff 0%, #fff8fb 70%, #fff5f8 100%), linear-gradient(135deg, rgba(255,193,212,0.95), rgba(255,139,167,0.7))',
          backgroundOrigin: 'padding-box, border-box',
          backgroundClip: 'padding-box, border-box',
          boxShadow: '0 14px 36px rgba(191, 95, 130, 0.1)',
        }}
      >
        <Tabs
          value={safeTabIndex}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            position: photosActive ? 'relative' : 'sticky',
            top: photosActive ? 0 : { xs: 56, sm: 64 },
            zIndex: (theme) => theme.zIndex.appBar - 1,
            bgcolor: '#fff8fb',
            borderBottom: 1,
            borderColor: alpha('#ff8ba7', 0.35),
            px: { xs: 0, sm: 2 },
            flexShrink: 0,
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': { opacity: 0.3 },
            },
            '& .MuiTabs-flexContainer': {
              justifyContent: 'flex-start',
            },
            '& .MuiTab-root': {
              minHeight: { xs: 52, sm: 48 },
              minWidth: 'auto',
              flex: '0 0 auto',
              px: { xs: 1, sm: 2 },
              maxWidth: 360,
              whiteSpace: 'nowrap',
              fontWeight: 700,
            },
            '& .MuiTab-root.Mui-selected': { color: '#e06a88' },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 999,
              background: 'linear-gradient(90deg, #ffc1d4, #ff8ba7, #f48fb1)',
            },
            '& .MuiTab-iconWrapper': {
              marginRight: { xs: 0.5, sm: 1 },
              marginBottom: '0 !important',
            },
          }}
        >
          {showControlTab && (
            <Tab
              icon={<SettingsRemoteIcon />}
              iconPosition="start"
              label={isMobile ? t.deviceMgmt.tabs.controlShort : t.deviceMgmt.tabs.control}
              aria-label={t.deviceMgmt.tabs.control}
            />
          )}
          {showPhotos && (
            <Tab
              icon={<ImageIcon />}
              iconPosition="start"
              label={isMobile ? t.deviceMgmt.tabs.photosShort : t.deviceMgmt.tabs.photos}
              aria-label={t.deviceMgmt.tabs.photos}
            />
          )}
          {showUpload && (
            <Tab
              icon={<PhoneIphoneIcon />}
              iconPosition="start"
              label={isMobile ? t.deviceMgmt.tabs.uploadShort : t.deviceMgmt.tabs.upload}
              aria-label={t.deviceMgmt.tabs.upload}
            />
          )}
          {showPhotobooth && (
            <Tab
              icon={<CameraAltIcon />}
              iconPosition="start"
              label={isMobile ? t.deviceMgmt.tabs.photoboothShort : t.deviceMgmt.tabs.photobooth}
              aria-label={t.deviceMgmt.tabs.photobooth}
            />
          )}
          {showEdit && (
            <Tab
              icon={<EditIcon />}
              iconPosition="start"
              label={isMobile ? t.deviceMgmt.tabs.editShort : t.deviceMgmt.tabs.edit}
              aria-label={t.deviceMgmt.tabs.edit}
            />
          )}
          {showQrEdit && (
            <Tab
              icon={<QrCode2Icon />}
              iconPosition="start"
              label={isMobile ? t.deviceMgmt.tabs.qrEditShort : t.deviceMgmt.tabs.qrEdit}
              aria-label={t.deviceMgmt.tabs.qrEdit}
            />
          )}
          {showColorApps && (
            <Tab
              icon={<PaletteIcon />}
              iconPosition="start"
              label={isMobile ? t.deviceMgmt.tabs.colorAppsShort : t.deviceMgmt.tabs.colorApps}
              aria-label={t.deviceMgmt.tabs.colorApps}
            />
          )}
        </Tabs>

            {tabKeys[safeTabIndex] === 'photos' && (
              <Box
                id="machine-photos-scroll"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  p: { xs: 1.25, sm: 2 },
                }}
              >
                <MachineScopedProPhotos
                  machineCode={machineData.machineCode}
                  appProduct={machineData.appProduct}
                  scrollableTarget="machine-photos-scroll"
                />
              </Box>
            )}

            {tabKeys[safeTabIndex] === 'upload' && (
          <Box p={{ xs: 2, sm: 4 }}>
            {/* Machine Info */}
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t.deviceMgmt.machineCodeLabel}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {machineData.machineCode}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* QR Code Upload + Random code */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                {t.deviceMgmt.qrUploadTitle}
              </Typography>
              <MachineQrCard
                value={uploadPageUrl}
                onOpen={() => window.open(uploadPageUrl, '_blank')}
              />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 0.5,
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                  }}
                >
                  {t.deviceMgmt.randomCodeTitle}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.65,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: (th) => alpha(th.palette.primary.main, 0.35),
                    bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 800,
                      fontSize: '1.35rem',
                      letterSpacing: '0.12em',
                      color: 'primary.dark',
                      lineHeight: 1,
                    }}
                  >
                    {machineData.randomCode}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(machineData!.randomCode, 'Random Code')}
                    sx={{ color: 'primary.main', p: 0.5 }}
                  >
                    {copied ? <CheckCircleIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>

              <Box textAlign="center">
                <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                  <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopy(uploadPageUrl, 'link')} sx={pinkOutlineBtnSx}>
                    {t.deviceMgmt.copyLink}
                  </Button>
                  <Button variant="outlined" size="small"
                    onClick={() => window.open(uploadPageUrl, '_blank')} sx={pinkOutlineBtnSx}>
                    {t.deviceMgmt.openNewTab}
                  </Button>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />
            <MobileUploadDeviceLimit
              machineCode={machineData.machineCode}
              appProduct={machineData.appProduct}
            />

            <Box sx={{ mt: 2, mb: 1 }}>
              <StaffUnlimitedQrCard
                machineCode={machineData.machineCode}
                purpose="mobile-upload"
              />
            </Box>

            {/* Actions */}
            <Box display="flex" gap={2} mt={4}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadMachineData} fullWidth>
                {t.common.refresh}
              </Button>
            </Box>
          </Box>
            )}

            {tabKeys[safeTabIndex] === 'control' && (
          <Box p={{ xs: 2, sm: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 2 }}>
              {t.deviceMgmt.controlQrTitle}
            </Typography>

            <MachineQrCard
              value={controlPageUrl}
              onOpen={() => window.open(controlPageUrl, '_blank')}
            />

            <Box textAlign="center">
              <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />}
                  onClick={() => handleCopy(controlPageUrl, 'link')} sx={pinkOutlineBtnSx}>
                  {t.deviceMgmt.copyLink}
                </Button>
                <Button variant="outlined" size="small"
                  onClick={() => window.open(controlPageUrl, '_blank')} sx={pinkOutlineBtnSx}>
                  {t.deviceMgmt.openNewTab}
                </Button>
              </Box>
            </Box>
          </Box>
            )}

            {tabKeys[safeTabIndex] === 'edit' && (
          <Box p={{ xs: 2, sm: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 2 }}>
              {t.deviceMgmt.editQrTitle}
            </Typography>

            <MachineQrCard
              value={editPageUrl}
              onOpen={() => window.open(editPageUrl, '_blank')}
            />

            <Box textAlign="center">
              <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />}
                  onClick={() => handleCopy(editPageUrl, 'link')} sx={pinkOutlineBtnSx}>
                  {t.deviceMgmt.copyLink}
                </Button>
                <Button variant="outlined" size="small"
                  onClick={() => window.open(editPageUrl, '_blank')} sx={pinkOutlineBtnSx}>
                  {t.deviceMgmt.openNewTab}
                </Button>
              </Box>
            </Box>
          </Box>
            )}

            {tabKeys[safeTabIndex] === 'qrEdit' && (
              <Box p={3}>
                <MachineQrPositionEditor machineCode={machineData.machineCode} />
              </Box>
            )}

            {tabKeys[safeTabIndex] === 'colorApps' && (
              <Box p={3}>
                {!showUpload && !showControl && !showEdit && !showPhotos && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t.deviceMgmt.noQrFeatures}
                  </Alert>
                )}
                <AppAccentColorApps machineCode={machineData.machineCode} />
              </Box>
            )}

            {tabKeys[safeTabIndex] === 'photobooth' && (
              <Box p={3}>
                <PhotoboothSimpleMode machineCode={machineData.machineCode} />
              </Box>
            )}
      </Paper>

      <Dialog
        open={remoteAction !== null}
        onClose={() => !remoteSending && setRemoteAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {remoteAction === 'shutdown'
            ? t.deviceMgmt.shutdownConfirmTitle
            : t.deviceMgmt.restartConfirmTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {remoteAction === 'shutdown'
              ? t.deviceMgmt.shutdownConfirmBody
              : t.deviceMgmt.restartConfirmBody}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoteAction(null)} disabled={remoteSending}>
            {t.common.cancel}
          </Button>
          <Button
            variant="contained"
            color={remoteAction === 'shutdown' ? 'error' : 'warning'}
            disabled={remoteSending}
            onClick={() => remoteAction && sendRemoteAppCommand(remoteAction)}
          >
            {remoteAction === 'shutdown' ? t.deviceMgmt.shutdownApp : t.deviceMgmt.restartApp}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MobileUploadMachineDetail;

